import {
  GridRenderCellParams,
  GridActionsCellItem,
  GridRowParams,
  GridColumnHeaderParams,
} from "@mui/x-data-grid";
import { renderDate } from "../../../formatting/dateFormat";
import { standardizeAndMagnifyEffectHtmlImages } from "../../../components/datagrid/renderers/htmlColumn";
import { trimWithEllipses } from "../../../components/datagrid/formatting";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import { ExtendedGridColDef } from "../../../components/datagrid/overrides";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  useGridSelector,
  gridDetailPanelExpandedRowIdsSelector,
} from "@mui/x-data-grid-premium";
import {
  MultiSelectCell,
  MultiSelectEditCell,
  getMultiSelectFilterOperators,
} from "../../../components/datagrid/MultiSelectCell";
import { BuyBoxInfoCell } from "./customRenderCellComponents/BuyBoxInfoCell";
import BuyboxInfoLegend from "./tooltips/BuyboxInfo";
import { getBuyBoxInfoFilterOperators } from "../../../components/datagrid/BuyBoxInfoFilter";

import allianceIcon from "../../../assets/icons/alliance.png";
import amazonIcon from "../../../assets/icons/amazon.png";
import ccvideoIcon from "../../../assets/icons/ccvideo.png";
import dealsareusIcon from "../../../assets/icons/dealsareus.png";
import deepdiscountIcon from "../../../assets/icons/deepdiscount.png";
import discountEntertainmentIcon from "../../../assets/icons/discount_entertainment.png";
import ebayBlowitIcon from "../../../assets/icons/ebayblowit.png";
import heartlandIcon from "../../../assets/icons/heartland.png";
import importcdsIcon from "../../../assets/icons/importcds.png";
import milaIcon from "../../../assets/icons/mila.png";
import gruvIcon from "../../../assets/icons/gruv.png";
import wildcard1Icon from "../../../assets/icons/wildcard1.png";
import wildcard2Icon from "../../../assets/icons/wildcard2.png";
import rarewavesIcon from "../../../assets/icons/rarewaves.png";
import targetIcon from "../../../assets/icons/target.png";
import veiIcon from "../../../assets/icons/vei.png";
import walmartIcon from "../../../assets/icons/walmart.png";
import wowhdIcon from "../../../assets/icons/wowhd.png";

const renderStoreHeader = (icon: string) => (params: GridColumnHeaderParams) =>
  (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        lineHeight: 1.2,
      }}
    >
      <img
        src={icon}
        alt={params.colDef.headerName}
        height="20px"
        width="20px"
      />
      <span>{params.colDef.headerName}</span>
    </div>
  );

const cloudfrontUrl = process.env.REACT_APP_CLOUDFRONT_URL;

const ExpandButton = ({ rowId }: { rowId: any }) => {
  const apiRef = useGridApiContext();
  const expandedRowIds = useGridSelector(
    apiRef,
    gridDetailPanelExpandedRowIdsSelector
  );
  const isExpanded = Array.from(expandedRowIds).includes(rowId);

  return (
    <GridActionsCellItem
      icon={isExpanded ? <RemoveIcon /> : <AddIcon />}
      label={isExpanded ? "Collapse" : "Expand"}
      onClick={() => {
        apiRef.current.toggleDetailPanel(rowId);
      }}
    />
  );
};

export const columns: ExtendedGridColDef[] = [
  {
    field: "inventory_associated_listing_count_view.associated_listing_count",
    headerName: "",
    description: "",
    type: "actions",
    hideable: false,
    width: 50,
    getActions: (params: GridRowParams) => {
      const associatedCount =
        params.row.inventory_associated_listing_count_view
          ?.associated_listing_count ?? 0;

      // Only show expand button if there are associated listings
      if (associatedCount < 1) {
        return [];
      }

      return [<ExpandButton key="expand" rowId={params.id} />];
    },
  },
  {
    field: "supply.sort",
    headerName: "Sort",
    description: "User designated sort position",
    type: "number",
    valueGetter: (value, row) => row.supply?.sort,
    editable: true,
  },
  {
    field: "supply.upc",
    headerName: "UPC",
    description: "UPC(Universal Product Code)/ SKU (Stock Keeping Unit)",
    valueGetter: (value, row) => row.supply?.upc,
  },
  {
    field: "supply.html",
    headerName: "HTML",
    description: "General HTML column for row",
    valueGetter: (value, row) => row.supply?.html,
    renderCell: (params: GridRenderCellParams) => (
      <span
        onMouseDown={(e) => e.stopPropagation()}
        dangerouslySetInnerHTML={{
          __html: standardizeAndMagnifyEffectHtmlImages(params.value),
        }}
      />
    ),
    editable: true,
  },
  {
    field: "supply.supply_title",
    headerName: "Title",
    description: "Yom's title for item (not Amazon's)",
    minWidth: 250,
    maxWidth: 350,
    valueGetter: (value, row) => row.supply?.supply_title,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div
          style={{
            whiteSpace: "normal",
            overflow: "hidden",
            textOverflow: "clip",
            lineHeight: "1.4",
            wordBreak: "break-word",
          }}
        >
          {params.value}
        </div>
      );
    },
    editable: true,
  },
  {
    field: "supply.supply_asin",
    headerName: "ASIN",
    description: "Supply ASIN (Amazon Standard Identification Number)",
    valueGetter: (value, row) => row.supply?.supply_asin,
  },
  {
    field: "inventory.inventory_shelf",
    headerName: "Shelf",
    description: "Where the item can be found in our warehouse",
    valueGetter: (value, row) => row.inventory?.inventory_shelf,
    editable: true,
  },
  {
    field: "supply_families_view.families",
    headerName: "Families",
    description: "Product families this supply unit is a member of.",
    width: 250,
    disableAutosize: true,
    valueGetter: (value, row) => row.supply_families_view?.families,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={params.value}
        >
          {params.value}
        </div>
      );
    },
  },
  {
    field: "inventory.part_number",
    headerName: "Part Number",
    description: "Part number(s) for this inventory unit",
    valueGetter: (value, row) => row.inventory?.part_number,
  },
  {
    field: "supply.legacy_part_number",
    headerName: "Legacy Part Number",
    description:
      "Legacy part number from old system to act as guiding reference with new release",
    valueGetter: (value, row) => row.supply?.legacy_part_number,
  },
  {
    field: "inventory_with_formatted_id_view.formatted_inventory_id",
    headerName: "Inventory ID",
    description: "Inventory ID for this unit",
    valueGetter: (value, row) =>
      row.inventory_with_formatted_id_view?.formatted_inventory_id,
  },
  {
    field: "inventory_associated_product_asins_view.product_asins",
    headerName: "Product ASINs",
    description: "Product ASINs this inventory is associated with.",
    width: 300,
    disableAutosize: true,
    valueGetter: (value, row) =>
      row.inventory_associated_product_asins_view?.product_asins,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={params.value}
        >
          {params.value}
        </div>
      );
    },
  },
  {
    field: "supply.discontinued_date",
    headerName: "Discontinued",
    description: "Discontinued Date from alliance file",
    valueGetter: (value, row) =>
      row.supply?.discontinued_date
        ? new Date(row.supply?.discontinued_date)
        : null,
    type: "date",
  },
  {
    field: "media_types",
    headerName: "Media Types",
    description:
      "The media types in this product (Vinyl, Book, CD, DVD, Bluray, 4K)",
    type: "custom",
    valueGetter: (value, row) => row.media_types,
    renderCell: (params: GridRenderCellParams) => (
      <MultiSelectCell {...params} />
    ),
    renderEditCell: (params) => <MultiSelectEditCell {...params} />,
    filterOperators: getMultiSelectFilterOperators("media_types"),
    editable: true,
    minWidth: 150,
  },
  {
    field: "supply.studio",
    headerName: "Studio",
    description: "Studio who owns the rights to the title",
    valueGetter: (value, row) => row.supply?.studio,
    editable: true,
  },
  {
    field: "supply.ebay_itemid",
    headerName: "Ebay Item ID",
    description: "Ebay product ID",
    valueGetter: (value, row) => row.supply?.ebay_itemid,
    editable: true,
  },
  {
    field: "supply.walsku",
    headerName: "Walmart SKU",
    description: "Walmart SKU",
    valueGetter: (value, row) => row.supply?.walsku,
    editable: true,
  },
  {
    field: "supply.restock_status_ftp",
    type: "string",
    headerName: "Restock",
    description:
      "The availability of supply inventory needed to fulfill this selling option from our distributors. IS=In stock, BO=Back Order, OP=Out of Print",
    valueGetter: (value, row) => row.supply?.restock_status_ftp,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === "999999") return "Misthreading error.";
      if (data === "OP")
        return (
          <span style={{ color: "#CC0000", fontWeight: 700, fontSize: 17 }}>
            OP
          </span>
        );
      if (data === "BO")
        return (
          <span style={{ color: "#0066FF", fontWeight: 700, fontSize: 17 }}>
            BO
          </span>
        );
      if (data === "IS")
        return (
          <span style={{ color: "#339933", fontWeight: 700, fontSize: 17 }}>
            IS
          </span>
        );
      return null;
    },
  },
  {
    field: "supply.sup_lin",
    headerName: "Supplier Link",
    description:
      "Supplier hot links to selected products, or to suppliers not currently being scraped",
    width: 400,
    disableAutosize: true,
    valueGetter: (value, row) => row.supply?.sup_lin,
    renderCell: (params: GridRenderCellParams) => (
      <span
        onMouseDown={(e) => e.stopPropagation()}
        dangerouslySetInnerHTML={{
          __html: standardizeAndMagnifyEffectHtmlImages(params.value),
        }}
      />
    ),
    editable: true,
  },
  {
    field: "supply.listed",
    headerName: "Listed",
    description: "Listing department queue, and work orders",
    valueGetter: (value, row) => row.supply?.listed,
    editable: true,
  },
  {
    field: "supply.ono",
    headerName: "ONO",
    description:
      "ONO (On Order) Inventory that has been ordered through supplier, and has not yet been delivered to our warehouse",
    type: "number",
    valueGetter: (value, row) => row.supply?.ono,
  },
  {
    field: "inventory.ono_pooled",
    headerName: "ONO Pooled",
    description:
      "Pooled ONO (On Order) Inventory that has been ordered through supplier, and has not yet been delivered to our warehouse",
    type: "number",
    valueGetter: (value, row) => row.inventory?.ono_pooled,
  },
  {
    field: "inventory.avcg",
    headerName: "AVCG",
    description:
      "The average (mean) cost paid for the product in our warehouse.",
    type: "number",
    valueGetter: (value, row) => row.inventory?.avcg,
  },
  {
    field: "inventory.one_year_lowest_cost",
    headerName: "One Year Lowest COGS",
    description: "The lowest price paid for this unit in the last year",
    type: "number",
    valueGetter: (value, row) => row.inventory?.one_year_lowest_cost,
  },
  {
    field: "inventory.ai_pooled",
    headerName: "AI Pooled",
    description: "Inv + ONO Pooled",
    type: "number",
    valueGetter: (value, row) => row.inventory?.ai_pooled,
  },
  {
    field: "supply.house",
    headerName: "House",
    description: "Analyst/Purchaser work order",
    width: 500,
    disableAutosize: true,
    valueGetter: (value, row) => row.supply?.house,
    editable: true,
  },
  {
    field: "supply.house_2",
    headerName: "House 2",
    description: "Analyst/Purchaser work order",
    width: 500,
    disableAutosize: true,
    valueGetter: (value, row) => row.supply?.house_2,
    editable: true,
  },
  {
    field: "inventory.add_date",
    headerName: "Add Date",
    description: "When the product was last scanned into inventory",
    valueGetter: (value, row) => row.inventory?.add_date,
    valueFormatter: (value) => renderDate(value),
    type: "dateTime",
  },
  {
    field: "inventory.sub_date",
    headerName: "Sub Date",
    description: "When the product was last scanned out of inventory",
    valueGetter: (value, row) => row.inventory?.sub_date,
    valueFormatter: (value) => renderDate(value),
    type: "dateTime",
  },
  {
    field: "supply.inventory_id",
    headerName: "Supply Inventory ID",
    description: "Supply inventory ID",
    valueGetter: (value, row) => row.supply?.inventory_id,
  },
  {
    field: "inventory.inv",
    headerName: "Inv",
    description: "How much of this item is in our East Aurora warehouse",
    type: "number",
    valueGetter: (value, row) => row.inventory?.inv,
  },
  {
    field: "supply.dist_inv",
    headerName: "Dist Inv",
    description: "Alliance Entertainment Distributor inventory",
    type: "number",
    valueGetter: (value, row) => row.supply?.dist_inv,
  },
  {
    field: "supply.dau_inv",
    headerName: "DAU Inv",
    description:
      "Dealsareus Distributor inventory (Displayed as #currently on hand - #currently available from their supplier)",
    valueGetter: (value, row) => row.supply?.dau_inv,
  },
  {
    field: "supply.mila_inv",
    headerName: "Mila Inv",
    description: "Mila inventory available",
    type: "number",
    valueGetter: (value, row) => row.supply?.mila_inv,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Mila inv\n\nUpdated at: ${params.row.supply?.mila_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.discount_entertainment_inv",
    headerName: "DE Inv",
    description: "Discount Entertainment inventory available",
    type: "number",
    valueGetter: (value, row) => row.supply?.discount_entertainment_inv,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Discount Entertainment inv\n\nUpdated at: ${params.row.supply?.discount_entertainment_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.gruv_inv",
    headerName: "Gruv Inv",
    description: "Gruv supplier inventory available",
    type: "number",
    valueGetter: (value, row) => row.supply?.gruv_inv,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Gruv inv\n\nUpdated at: ${params.row.supply?.gruv_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.wildcard1_inv",
    headerName: "WC1 Inv",
    description: "Wildcard 1 supplier inventory available",
    type: "number",
    valueGetter: (value, row) => row.supply?.wildcard1_inv,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Wildcard1 inv\n\nUpdated at: ${params.row.supply?.wildcard1_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.wildcard2_inv",
    headerName: "WC2 Inv",
    description: "Wildcard 2 supplier inventory available",
    type: "number",
    valueGetter: (value, row) => row.supply?.wildcard2_inv,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Wildcard2 inv\n\nUpdated at: ${params.row.supply?.wildcard2_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.dmg_inv_display",
    headerName: "DMG Inv",
    description:
      "The quantity of damage products in order of damage reason 1-2-3.",
    valueGetter: (value, row) => row.supply?.dmg_inv_display,
  },
  {
    field: "inventory.prod_list_type",
    headerName: "Product List Type",
    description: "Product List Type",
    valueGetter: (value, row) => row.inventory?.prod_list_type,
    editable: true,
  },
  {
    field: "keepa_product_data.keepa_buybox_30_day_avg",
    headerName: "Keepa 30d",
    description: "Keepa 30 day buybox average",
    type: "number",
    valueGetter: (value, row) =>
      row.keepa_product_data?.keepa_buybox_30_day_avg,
    valueFormatter: (value) => {
      return value ? `$${Number(value).toFixed(2)}` : "";
    },
  },
  {
    field: "keepa_product_data.keepa_timestamp",
    headerName: "Keepa TimeStamp",
    description: "When was the data pulled from Keepa API",
    type: "date",
    valueGetter: (value, row) => row.keepa_product_data?.keepa_timestamp,
    valueFormatter: (value) => renderDate(value),
  },
  {
    field: "supply.analytics_db_column_1",
    headerName: "Analytics DB 1",
    description: "Analytic DB column 1",
    valueGetter: (value, row) => row.supply?.analytics_db_column_1,
  },
  {
    field: "supply.analytics_db_column_2",
    headerName: "Analytics DB 2",
    description: "Analytic DB column 2",
    valueGetter: (value, row) => row.supply?.analytics_db_column_2,
  },
  {
    field: "amazon_product_pricing_info.sales_rank",
    headerName: "Sales Rank",
    description: "Sales rank on Amazon's website",
    type: "number",
    valueGetter: (value, row) => row.amazon_product_pricing_info?.sales_rank,
  },
  {
    field: "amazon_product_pricing_info.product_category",
    headerName: "Product Category",
    description: "Product Category",
    valueGetter: (value, row) =>
      row.amazon_product_pricing_info?.product_category,
  },
  {
    field: "amazon_product_pricing_info.buy_box_price",
    headerName: "BuyBox",
    description: "The price that is currently listed under the buy box",
    type: "number",
    valueGetter: (value, row) => row.amazon_product_pricing_info?.buy_box_price,
    valueFormatter: (value) => {
      if (!value) return null;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    },
  },
  {
    field: "amazon_product_pricing_info.updated_at",
    headerName: "Buy Box Updated At",
    description: "When the buy box info was last updated",
    type: "date",
    valueGetter: (value, row) => row.amazon_product_pricing_info?.updated_at,
    valueFormatter: (value) => renderDate(value),
  },
  {
    field: "amazon_product_pricing_info.buy_box_info",
    headerName: "Buy Box Info",
    description:
      "Information about the buybox seller, ship time, and various other listing conditions.",
    type: "custom",
    renderHeader: () => <BuyboxInfoLegend />,
    filterOperators: getBuyBoxInfoFilterOperators(),
    valueGetter: (value, row) => row.amazon_product_pricing_info?.buy_box_info,
    renderCell: BuyBoxInfoCell,
  },
  {
    field: "supply.dtb1",
    headerName: "DTB1",
    description:
      "DTB (Direct to Buyer) Terminal or Landed Cost of a product (includes any volume discounts, promotions and rebates)",
    type: "number",
    valueGetter: (value, row) => row.supply?.dtb1,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      const supplierName = params.row.supply?.dtb1_supplier;
      return (
        <span>
          <img
            title={supplierName}
            height="16px"
            width="16px"
            src={`${cloudfrontUrl}${supplierName}.png`}
            alt={supplierName}
          />{" "}
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.dtb2",
    headerName: "DTB2",
    description:
      "DTB2 (Supplier with the 2nd lowest terminal price - see also DTB1)",
    type: "number",
    valueGetter: (value, row) => row.supply?.dtb2,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      const supplierName = params.row.supply?.dtb2_supplier;
      return (
        <span>
          <img
            title={supplierName}
            height="16px"
            width="16px"
            src={`${cloudfrontUrl}${supplierName}.png`}
            alt={supplierName}
          />{" "}
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.dtb3",
    headerName: "DTB3",
    description:
      "DTB3 (Supplier with the 3rd lowest terminal price - see also DTB1)",
    type: "number",
    valueGetter: (value, row) => row.supply?.dtb3,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      const supplierName = params.row.supply?.dtb3_supplier;
      return (
        <span>
          <img
            title={supplierName}
            height="16px"
            width="16px"
            src={`${cloudfrontUrl}${supplierName}.png`}
            alt={supplierName}
          />{" "}
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.dtb4",
    headerName: "DTB4",
    description:
      "DTB4 (Supplier with the 4th lowest terminal price - see also DTB1)",
    type: "number",
    valueGetter: (value, row) => row.supply?.dtb4,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      const supplierName = params.row.supply?.dtb4_supplier;
      return (
        <span>
          <img
            title={supplierName}
            height="16px"
            width="16px"
            src={`${cloudfrontUrl}${supplierName}.png`}
            alt={supplierName}
          />{" "}
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.dtb5",
    headerName: "DTB5",
    description:
      "DTB5 (Supplier with the 5th lowest terminal price - see also DTB1)",
    type: "number",
    valueGetter: (value, row) => row.supply?.dtb5,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      const supplierName = params.row.supply?.dtb5_supplier;
      return (
        <span>
          <img
            title={supplierName}
            height="16px"
            width="16px"
            src={`${cloudfrontUrl}${supplierName}.png`}
            alt={supplierName}
          />{" "}
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.dtb6",
    headerName: "DTB6",
    description:
      "DTB6 (Supplier with the 6th lowest terminal price - see also DTB1)",
    type: "number",
    valueGetter: (value, row) => row.supply?.dtb6,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      const supplierName = params.row.supply?.dtb6_supplier;
      return (
        <span>
          <img
            title={supplierName}
            height="16px"
            width="16px"
            src={`${cloudfrontUrl}${supplierName}.png`}
            alt={supplierName}
          />{" "}
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.dtb7",
    headerName: "DTB7",
    description:
      "DTB7 (Supplier with the 7th lowest terminal price - see also DTB1)",
    type: "number",
    valueGetter: (value, row) => row.supply?.dtb7,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      const supplierName = params.row.supply?.dtb7_supplier;
      return (
        <span>
          <img
            title={supplierName}
            height="16px"
            width="16px"
            src={`${cloudfrontUrl}${supplierName}.png`}
            alt={supplierName}
          />{" "}
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.dtb8",
    headerName: "DTB8",
    description:
      "DTB8 (Supplier with the 8th lowest terminal price - see also DTB1)",
    type: "number",
    valueGetter: (value, row) => row.supply?.dtb8,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      const supplierName = params.row.supply?.dtb8_supplier;
      return (
        <span>
          <img
            title={supplierName}
            height="16px"
            width="16px"
            src={`${cloudfrontUrl}${supplierName}.png`}
            alt={supplierName}
          />{" "}
          {data}
        </span>
      );
    },
  },
  {
    field: "supply.alliance",
    headerName: "Alliance",
    description: "Alliance Entertainment price",
    type: "number",
    renderHeader: renderStoreHeader(allianceIcon),
    valueGetter: (value, row) => row.supply?.alliance,
    editable: true,
  },
  {
    field: "supply.amazon",
    headerName: "Amazon",
    description: "Amazon supplier scraped price",
    type: "number",
    renderHeader: renderStoreHeader(amazonIcon),
    valueGetter: (value, row) => row.supply?.amazon,
    editable: true,
  },
  {
    field: "supply.ccvideo",
    headerName: "CCVideo",
    description: "CC Video supplier scraped price",
    type: "number",
    renderHeader: renderStoreHeader(ccvideoIcon),
    valueGetter: (value, row) => row.supply?.ccvideo,
    editable: true,
  },
  {
    field: "supply.dealsareus",
    headerName: "Dealsareus",
    description: "Deals Distributor Price",
    type: "number",
    renderHeader: renderStoreHeader(dealsareusIcon),
    valueGetter: (value, row) => row.supply?.dealsareus,
    editable: true,
  },
  {
    field: "supply.deepdiscount",
    headerName: "DeepDiscount",
    description: "Deep Discount supplier scraped price",
    type: "number",
    renderHeader: renderStoreHeader(deepdiscountIcon),
    valueGetter: (value, row) => row.supply?.deepdiscount,
    editable: true,
  },
  {
    field: "supply.discount_entertainment",
    headerName: "Discount Entertainment",
    description: "Discount Entertainment supplier scraped price",
    type: "number",
    renderHeader: renderStoreHeader(discountEntertainmentIcon),
    valueGetter: (value, row) => row.supply?.discount_entertainment,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Discount Entertainment price\n\nUpdated at: ${params.row.supply?.discount_entertainment_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
    editable: true,
  },
  {
    field: "supply.blowitebay",
    headerName: "EbayBlowIt",
    description: "Ebay BlowItOutofHere (Ebay Supplier) scraped price",
    type: "number",
    renderHeader: renderStoreHeader(ebayBlowitIcon),
    valueGetter: (value, row) => row.supply?.blowitebay,
    editable: true,
  },
  {
    field: "supply.inetvideo",
    headerName: "HeartLand",
    description:
      "InetVideo Music scraped price (Website / Supplier has ceased operations)",
    type: "number",
    renderHeader: renderStoreHeader(heartlandIcon),
    valueGetter: (value, row) => row.supply?.inetvideo,
    editable: true,
  },
  {
    field: "supply.importcds",
    headerName: "ImportCDs",
    description: "Import CDs supplier scraped price",
    type: "number",
    renderHeader: renderStoreHeader(importcdsIcon),
    valueGetter: (value, row) => row.supply?.importcds,
    editable: true,
  },
  {
    field: "supply.mila",
    headerName: "Mila",
    description: "Mila supplier scraped price",
    type: "number",
    renderHeader: renderStoreHeader(milaIcon),
    valueGetter: (value, row) => row.supply?.mila,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Mila price\n\nUpdated at: ${params.row.supply?.mila_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
    editable: true,
  },
  {
    field: "supply.gruv",
    headerName: "Gruv",
    description:
      "Gruv supplier scraped price (Website / Supplier has ceased operations)",
    type: "number",
    renderHeader: renderStoreHeader(gruvIcon),
    valueGetter: (value, row) => row.supply?.gruv,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Gruv price\n\nUpdated at: ${params.row.supply?.gruv_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
    editable: true,
  },
  {
    field: "supply.wildcard1",
    headerName: "Wildcard1",
    description: "Wildcard1 product price",
    type: "number",
    renderHeader: renderStoreHeader(wildcard1Icon),
    valueGetter: (value, row) => row.supply?.wildcard1,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Wildcard1 price\n\nUpdated at: ${params.row.supply?.wildcard1_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
    editable: true,
  },
  {
    field: "supply.wildcard2",
    headerName: "Wildcard2",
    description: "Wildcard2 product price",
    type: "number",
    renderHeader: renderStoreHeader(wildcard2Icon),
    valueGetter: (value, row) => row.supply?.wildcard2,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Wildcard2 price\n\nUpdated at: ${params.row.supply?.wildcard2_data_timestamp}`}
        >
          {data}
        </span>
      );
    },
    editable: true,
  },
  {
    field: "supply.rarewaves",
    headerName: "Rarewaves",
    description: "Rarewaves supplier scraped price",
    type: "number",
    renderHeader: renderStoreHeader(rarewavesIcon),
    valueGetter: (value, row) => row.supply?.rarewaves,
    editable: true,
  },
  {
    field: "supply.target_store",
    headerName: "Target",
    description:
      "Target supplier scraped price (Website / Supplier has ceased operations)",
    type: "number",
    renderHeader: renderStoreHeader(targetIcon),
    valueGetter: (value, row) => row.supply?.target_store,
    editable: true,
  },
  {
    field: "supply.vei",
    headerName: "VEI",
    description: "VEI scraped price (Website / Supplier has ceased operations)",
    type: "number",
    renderHeader: renderStoreHeader(veiIcon),
    valueGetter: (value, row) => row.supply?.vei,
    editable: true,
  },
  {
    field: "supply.walmart",
    headerName: "Walmart",
    description: "Walmart Supplier Scraped Price",
    type: "number",
    renderHeader: renderStoreHeader(walmartIcon),
    valueGetter: (value, row) => row.supply?.walmart,
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return "";
      return (
        <span
          title={`Walmart price\n\nUpdated at: ${renderDate(
            params.row.supply?.walmart_updated_timestamp
          )}`}
        >
          {data}
        </span>
      );
    },
    editable: true,
  },
  {
    field: "supply.wowhd",
    headerName: "WOWHD",
    description: "WowHD Supplier Scraped Price",
    type: "number",
    renderHeader: renderStoreHeader(wowhdIcon),
    valueGetter: (value, row) => row.supply?.wowhd,
    editable: true,
  },
  {
    field: "supply.supply_notes",
    headerName: "Notes",
    description: "General note column for supply / inventory rows",
    valueGetter: (value, row) => row.supply?.supply_notes,
    renderCell: (params: GridRenderCellParams) => {
      return (
        <div
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={params.value}
        >
          {params.value}
        </div>
      );
    },
    editable: true,
  },
  {
    field: "supply.threading_notes",
    headerName: "Threading Notes",
    description: "Threading Notes",
    valueGetter: (value, row) => row.supply?.threading_notes,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    editable: true,
  },
  {
    field: "supply.inventory_notes",
    headerName: "Inventory Notes",
    description: "Inventory Notes",
    valueGetter: (value, row) => row.supply?.inventory_notes,
    renderCell: (params: GridRenderCellParams) => {
      return trimWithEllipses(params.value, 250);
    },
    editable: true,
  },
  {
    field: "supply.created_at",
    headerName: "Created At",
    description: "When this supply row was created in YOM's system",
    type: "date",
    valueGetter: (value, row) => row.supply?.created_at,
    valueFormatter: (value) => renderDate(value),
  },
  {
    field: "inventory.short_term_demand_trend_percentage",
    headerName: "ST Demand %",
    description: "Inventory Demand Short Term Demand Trend Percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;
      return `${data}%`;
    },
    valueGetter: (value, row) =>
      row.inventory?.short_term_demand_trend_percentage,
  },
  {
    field: "inventory.medium_term_demand_trend_percentage",
    headerName: "MT Demand %",
    description: "Inventory Demand Medium Term Demand Trend Percentage",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (data === undefined || data === null) return null;
      return `${data}%`;
    },
    valueGetter: (value, row) =>
      row.inventory?.medium_term_demand_trend_percentage,
  },
  {
    field: "inventory.demand_7d",
    headerName: "Demand 7d",
    description: "Inventory Demand 7 Day",
    type: "number",
    valueGetter: (value, row) => row.inventory?.demand_7d,
  },
  {
    field: "inventory.demand_30d",
    headerName: "Demand 30d",
    description: "Inventory Demand 30 Day",
    type: "number",
    valueGetter: (value, row) => row.inventory?.demand_30d,
  },
  {
    field: "inventory.demand_90d",
    headerName: "Demand 90d",
    description: "Inventory Demand 90 Day",
    type: "number",
    valueGetter: (value, row) => row.inventory?.demand_90d,
  },
  {
    field: "inventory.demand_365d",
    headerName: "Demand 365d",
    description: "Inventory Demand 365 Day",
    type: "number",
    valueGetter: (value, row) => row.inventory?.demand_365d,
  },
  {
    field: "inventory.demand_365d_90d_30d_7d",
    headerName: "Demand All",
    description: "Inventory Demand 365-90-30-7 Day",
    valueGetter: (value, row) => row.inventory?.demand_365d_90d_30d_7d,
  },
  {
    field: "inventory.profit_30d",
    headerName: "30 Day Profit",
    description: "Inventory 30 Day Profit",
    type: "number",
    renderCell: (params: GridRenderCellParams) => {
      const data = params.value;
      if (!data) return null;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(data);
    },
    valueGetter: (value, row) => row.inventory?.profit_30d,
  },
  {
    field: "inventory.inventory_purchase_recommendation",
    headerName: "IPR",
    description:
      "Inventory Purchase Recommendation (3 * Demand 30d) - AI Pooled - New Ship Stock)",
    type: "number",
    valueGetter: (value, row) =>
      row.inventory?.inventory_purchase_recommendation,
  },
  {
    field: "inventory_active_listing_count_view.active_count",
    headerName: "Active Listings",
    description: "Count of FBA listings that have sold in the last 90 days",
    type: "number",
    valueGetter: (value, row) =>
      row.inventory_active_listing_count_view?.active_count,
  },
];

export const nestedTableColumns: string[] = [
  "product.sort",
  "product.msku",
  "product.product_asin",
  "product.status",
  "product_families_view.families",
  "product.html",
  "product.assembly_part_number",
  "product.product_title",
  "product_listings.total_sales",
  "product.page_sales_365_90_30_7",
  "product_listings.last_non_cancelled_order_timestamp",
  "product.buy_box_markup_display",
  "amazon_product_pricing_info.buy_box_info",
  "amazon_product_pricing_info.buy_box_price",
  "product_listings.price",
  "product.an",
  "product.yom_purchase_rec",
  "product.master_analyst_inv",
  "product.ats",
  "product.ats_last_increase_timestamp",
  "product_listings.30_day_profit",
  "product.ship_stock",
  "product.stock",
  "product.yom_ship_rec_holiday",
  "product.yom_ship_rec",
  "product.amz_recommended_ship_quantity",
  "product.yom_ship_rec_amz_ship_rec_diff",
  "product.30_day_restock_in_stock_count",
  "product.restock_status_calculated",
  "product_listings.merchant_available",
  "product_listings.purchasable_stock",
  "product.master_avg_cost_of_goods",
  "product.master_avg_cogs_ninety_day",
  "product.7_day_sales",
  "product.one_year_lowest_cost",
  "product.replacement_cost",
  "product.replacement_cost_dist",
  "product.cogs",
  "product.ysr_nss_rec",
  "product.analytics_db_column_1",
  "product.analytics_db_column_2",
  "product.analytics_db_column_3",
  "product.analytics_db_column_4",
  "product.profit",
  "product.margin",
  "product.margin_30_pricing",
  "product.margin_15_pricing",
  "product.margin_0_pricing",
  "product.cogs_update_timestamp",
  "product.next_amz_ship_qty",
  "product.amz_ship_qty",
  "product.amz_queue",
  "product.pick_status",
  "product.fba_sell_through",
  "product.alert",
  "product_media_types_view.media_types",
  "product.component_count",
  "product.yom_handling_cost_adjustment",
  "product.packaged_weight_ounces",
  "product.yom_handling_cost_adjustment",
  "product.orders",
  "product.shelf",
  "product.wn",
  "product.wwo",
  "product.proceeds",
  "product_listings.open_date",
  "product.retired",
  "product.retired_timestamp",
  "product.weeks_until_depleted",
  "product.amazon_stock_weeks_until_depleted",
  "product.master_quantity_on_order",
  "product.roi",
  "product.last_ninety_day_avg_cogs",
  "product.latest_timestamp",
  "product.30_day_sales",
  "product.type",
  "product.genre",
  "product.card_number",
  "product.ship_weight",
  "product.fnsku",
  "product.part_number",
  "product.listory",
  "product.prod_list_type",
  "product.inv_age_271_to_365",
  "product_profit_projection_view.yom_ship_rec_potential_profit",
  "product.fba_latest_shipment_quantity",
  "product.90_day_sales",
  "amazon_product_pricing_info.sales_rank",
];
